import * as React from "react";
import styled from "styled-components";
import { Colors } from "@blueprintjs/core";

import { DayInfos, LAST_HOUR_OF_DAY } from "../utils/dates";
import {
  addHours,
  differenceInMilliseconds,
  endOfDay,
  isAfter,
  isBefore,
  startOfDay,
  subHours,
} from "date-fns";
import { CalendarEvent, useCalendarContext } from "../CalendarContext";

export interface DayCalendarEventProps {
  className?: string;
  style?: React.CSSProperties;
  event: CalendarEvent<any>;
  day: DayInfos;
}

const Container = styled.div`
  position: absolute;
  width: 100%;
  padding: 2px;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const CustomCard = styled.div`
  padding: 5px;
  flex: 1;
  //background-color: ${Colors.BLUE5};
  color: ${Colors.WHITE};
`;

export const DayCalendarEvent: React.FunctionComponent<
  DayCalendarEventProps
> = ({ className, event, day }) => {
  const { sliceHeightInPx, daySliceLengthInMs, renderEvent, hourLimit } =
    useCalendarContext();
  const beginOfTheDay = React.useMemo(() => hourLimit ? addHours(startOfDay(day.date), hourLimit.startHour) :startOfDay(day.date) , [day.date]);
  const endOfTheDay = React.useMemo(() => hourLimit ? subHours(endOfDay(day.date), LAST_HOUR_OF_DAY - hourLimit.endHour) : endOfDay(day.date), [day.date]);

  const getRelativePixelPositionFromStart = React.useCallback(
    (date: Date) => {
      const startMsFromStartOfDay = differenceInMilliseconds(
        date,
        beginOfTheDay
      );
      return Math.floor(
        (sliceHeightInPx / daySliceLengthInMs) * Math.abs(startMsFromStartOfDay)
      );
    },
    [beginOfTheDay, daySliceLengthInMs, sliceHeightInPx]
  );

  const position = React.useMemo(() => {
    const startDate = isBefore(event.startDate, beginOfTheDay)
      ? beginOfTheDay
      : event.startDate;

    const endDate = isAfter(event.endDate, endOfTheDay)
      ? endOfTheDay
      : event.endDate;

    const top = getRelativePixelPositionFromStart(startDate);
    const height = getRelativePixelPositionFromStart(endDate) - top;

    return { top, height };
  }, [
    beginOfTheDay,
    endOfTheDay,
    event.endDate,
    event.startDate,
    getRelativePixelPositionFromStart,
  ]);

  return (
    <Container
      className={className}
      style={{
        top: position.top,
        height: position.height,
        cursor: "move",
      }}
    >
      <CustomCard>{renderEvent(event.obj)}</CustomCard>
    </Container>
  );
};
